import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Brain, Flame, Loader2, CheckCircle2, Sparkles, X } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import MindsetWeeklyRecap, { MindsetMonthlyRecap } from '../components/MindsetWeeklyRecap';

const ORANGE_GLOW = '0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45)';

/**
 * 🧠 MINDSET — Daily Reflection page (/mindset)
 *
 * Yuri spec 2026-05-06:
 *   • Centralize Mindset on Dashboard as the 4th pillar
 *   • One rotating reflection question per day (community-wide rotation)
 *   • Answering "lights up" today's Mindset bar in the streak histogram
 *   • Streak badges (7-Day Clarity, Iron Mind 30) earned automatically
 */
export default function Mindset() {
  const navigate = useNavigate();
  const { language, t: _t } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const es = (language || 'fr').startsWith('es');
  // Iter. 411.mm2 — Trilingual question text with graceful fallback:
  // pick user's language, fall back FR → EN.
  const pickQText = (q) => q ? (es ? (q.es || q.en || q.fr) : (fr ? q.fr : (q.en || q.fr))) : '';
  const [data, setData] = useState(null);
  const [history, setHistory] = useState({ bars: [], streak_days: 0, total_answered: 0 });
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [coachReply, setCoachReply] = useState(null);  // {message, tone, points_earned, streak_days}

  const fetchData = async (opts = {}) => {
    try {
      // Trigger prefetch once on mount — generates AI question in background
      // and returns the cached one if already available.
      const todayCall = opts.skipPrefetch
        ? api.get('/mindset/today')
        : api.post('/mindset/prefetch').catch(() => api.get('/mindset/today'));

      const [today, hist] = await Promise.all([
        todayCall,
        api.get('/mindset/history?days=14'),
      ]);

      // /prefetch returns {ready, question, date} — fetch /today right after
      // to also get the `answered` flag.
      const todayPayload = today?.data || {};
      const needTodayCall = todayPayload.ready !== undefined; // came from /prefetch
      let merged = todayPayload;
      if (needTodayCall) {
        const t2 = await api.get('/mindset/today').catch(() => ({ data: {} }));
        merged = {
          ...todayPayload,
          ...(t2.data || {}),
          // Keep the freshest question (prefer the personalized one if ready)
          question: (t2.data && t2.data.question) || todayPayload.question,
          is_personalized: !!(t2.data && t2.data.is_personalized) || !!todayPayload.ready,
          _ai_pending: todayPayload.ready === false,
        };
      }

      setData(merged);
      setHistory(hist.data);
      if (merged?.answer) setAnswer(merged.answer);

      // Iter. 406.fr.32 — Aggressive AI swap polling.
      // The backend's AI generation takes ~8-12s, so a single 4s retry was
      // missing the swap. We now poll 3 times (4s / 10s / 16s) and give up
      // gracefully if the AI never comes back. Each retry stops the chain
      // as soon as it sees `is_personalized: true`.
      const isPending = merged?._ai_pending || merged?.ai_pending;
      if (isPending && !merged?.is_personalized) {
        const trySwap = async () => {
          try {
            const res = await api.get('/mindset/today');
            if (res?.data?.is_personalized) {
              setData((prev) => ({ ...(prev || {}), ...res.data, _ai_pending: false }));
              return true;
            }
          } catch { /* ignore */ }
          return false;
        };
        setTimeout(async () => {
          if (await trySwap()) return;
          setTimeout(async () => {
            if (await trySwap()) return;
            setTimeout(trySwap, 6000); // last attempt at 16s
          }, 6000); // second attempt at 10s
        }, 4000); // first attempt at 4s
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const submit = async () => {
    if (!answer.trim()) {
      toast.error(fr ? 'Écris ta réflexion' : 'Write your reflection');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/mindset/answer', { answer: answer.trim() });
      const pts = res.data.points_earned || 0;
      const fb = res.data.coach_feedback;

      // Show the AI coach reply as a styled modal (priority over toast)
      if (fb && fb.message) {
        setCoachReply({
          message: fb.message,
          tone: fb.tone || 'empathic',
          points_earned: pts,
          streak_days: res.data.streak_days || 0,
        });
      } else if (pts > 0) {
        toast.success(fr ? `+${pts} XP — Réflexion enregistrée 🧠` : `+${pts} XP — Reflection saved 🧠`);
      } else {
        toast.success(fr ? 'Réflexion mise à jour' : 'Reflection updated');
      }

      (res.data.new_badges || []).forEach(b => toast.success(`🏅 ${b.name_fr || b.name_en || b.id}`));
      await fetchData({ skipPrefetch: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || (fr ? 'Erreur' : 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const streak = history.streak_days || 0;

  // 14-day "Mindset success" histogram — reuses the orange-fluo Micro-Victory
  // signature (same glow + dashed cible look as the Walking page).
  const max = useMemo(() => 1, []);

  const question = data?.question;
  const questionText = pickQText(question);

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="mindset-page">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-9 h-9 rounded-full bg-secondary/60 flex items-center justify-center hover:bg-secondary transition-colors"
            data-testid="mindset-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold">
              {fr ? 'Rituel Mindset' : 'Mindset Ritual'}
            </p>
            <h1 className="font-heading text-2xl uppercase tracking-tight text-foreground">
              {fr ? 'Réflexion du Jour' : "Today's Reflection"}
            </h1>
          </div>
          {/* Streak chip */}
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ring-1 ${
              streak >= 3
                ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/20 ring-amber-400/50'
                : 'bg-secondary/60 ring-border/40'
            }`}
            data-testid="mindset-streak-chip"
          >
            <Flame className={`w-3.5 h-3.5 ${streak >= 3 ? 'text-amber-400' : 'text-muted-foreground'}`} />
            <span className={`text-xs font-black ${streak >= 3 ? 'text-amber-300' : 'text-muted-foreground'}`}>
              {streak}{fr ? 'j' : 'd'}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
          </div>
        ) : (
          <>
            {/* Iter. 406.fr.24 — Reorder layout per user request:
                1. Question du jour, 2. Bilan semaine, 3. Constance 14j,
                4. Bilan mois. Each recap stays self-hideable. */}

            {/* Question card */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <Card
                className="border-amber-400/40 bg-gradient-to-br from-orange-500/10 via-card to-amber-500/5 mb-5"
                style={{ boxShadow: ORANGE_GLOW }}
                data-testid="mindset-question-card"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-500/20 ring-1 ring-amber-400/50 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-amber-300" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
                          {fr ? 'Ta question d’aujourd’hui' : 'Your question today'}
                        </p>
                        {data?.is_personalized ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-500/25 to-amber-500/20 ring-1 ring-orange-400/50"
                            style={{ boxShadow: '0 0 8px rgba(251,146,60,0.45)' }}
                            data-testid="mindset-ai-badge"
                          >
                            <Sparkles className="w-2.5 h-2.5 text-orange-300" />
                            <span className="text-[9px] font-black uppercase tracking-widest text-orange-200 leading-none">
                              {fr ? 'Personnalisé YUMIX' : 'YUMIX-tailored'}
                            </span>
                          </span>
                        ) : data?._ai_pending ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 ring-1 ring-amber-400/40"
                            data-testid="mindset-ai-pending"
                          >
                            <Loader2 className="w-2.5 h-2.5 animate-spin text-amber-400" />
                            <span className="text-[9px] font-bold uppercase tracking-widest text-amber-300 leading-none">
                              {fr ? 'IA en cours…' : 'AI generating…'}
                            </span>
                          </span>
                        ) : null}
                      </div>
                      <p className="text-lg font-heading text-foreground leading-snug" data-testid="mindset-question-text">
                        {questionText}
                      </p>
                      {data?.is_personalized && data?.question?.theme && (
                        <p className="text-[9px] uppercase tracking-widest text-orange-300/70 font-bold mt-1">
                          {fr ? 'Thème' : 'Theme'} · {data.question.theme}
                        </p>
                      )}
                      {/* Iter. 406.fr.33 — Memory chip. Surfaces the fact that
                          the AI READ the user's recent reflections to craft the
                          question. The context_hint (the AI's own rationale)
                          shows as a tooltip on hover/long-press. Counts up to 5
                          recent answered reflections (matches the prompt's
                          memory window). */}
                      {data?.is_personalized && history?.total_answered > 0 && (() => {
                        const memCount = Math.min(history.total_answered, 5);
                        const hint = data?.question?.context_hint || '';
                        return (
                          <p
                            className="inline-flex items-center gap-1.5 mt-2 px-2 py-1 rounded-full bg-cyan-500/10 ring-1 ring-cyan-500/30 text-[10px] font-medium text-cyan-300 leading-none"
                            title={hint}
                            data-testid="mindset-memory-chip"
                          >
                            <Brain className="w-3 h-3" />
                            <span>
                              {fr
                                ? `Inspiré par tes ${memCount} dernière${memCount > 1 ? 's' : ''} réflexion${memCount > 1 ? 's' : ''}`
                                : `Drawn from your last ${memCount} reflection${memCount > 1 ? 's' : ''}`}
                            </span>
                          </p>
                        );
                      })()}
                    </div>
                  </div>

                  <Textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={fr ? 'Écris ta réponse, même 1 phrase compte...' : 'Write your reply, even 1 sentence counts...'}
                    rows={4}
                    maxLength={2000}
                    className="bg-zinc-900/60 border-amber-500/20 focus-visible:ring-amber-400/40"
                    data-testid="mindset-answer-input"
                  />
                  <div className="mt-1.5 text-[10px] text-muted-foreground text-right">
                    {answer.length}/2000
                  </div>

                  <Button
                    onClick={submit}
                    disabled={saving || !answer.trim()}
                    className="mt-3 w-full bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-600 text-white font-bold uppercase tracking-widest"
                    style={{ boxShadow: ORANGE_GLOW }}
                    data-testid="mindset-submit-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : data?.answered ? (
                      <><CheckCircle2 className="w-4 h-4 mr-2" />{fr ? 'Mettre à jour' : 'Update'}</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />{fr ? 'Valider mon Mindset' : 'Validate my Mindset'}</>
                    )}
                  </Button>

                  {data?.answered && (
                    <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-amber-400 font-bold" data-testid="mindset-answered-tag">
                      ✓ {fr ? 'Mindset du jour enregistré' : "Today's Mindset saved"}
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Iter. 406.fr.35 — Quick link to the full reflections journal.
                Only shown once the user has at least one logged reflection
                (otherwise the journal is empty and the link feels useless). */}
            {history?.total_answered > 0 && (
              <button
                onClick={() => navigate('/mindset/journal')}
                className="w-full mb-5 flex items-center justify-between px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/5 ring-1 ring-amber-500/30 hover:ring-amber-400/60 transition-all group"
                data-testid="mindset-open-journal-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/15 ring-1 ring-amber-500/30 flex items-center justify-center">
                    <Brain className="w-4 h-4 text-amber-300" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold uppercase tracking-widest text-amber-200">
                      {fr ? 'Mes réflexions' : 'My reflections'}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {fr
                        ? `Relis tes ${history.total_answered} réflexion${history.total_answered > 1 ? 's' : ''} — filtre par thème, cherche un détail.`
                        : `Re-read your ${history.total_answered} reflection${history.total_answered > 1 ? 's' : ''} — filter by theme, search a detail.`}
                    </p>
                  </div>
                </div>
                <span className="text-amber-400 text-lg group-hover:translate-x-0.5 transition-transform">›</span>
              </button>
            )}

            {/* 2. Bilan de la semaine — placed right after today's question */}
            <MindsetWeeklyRecap />

            {/* 14-day streak histogram */}
            <Card className="border-border/50 bg-card/60 mb-5" data-testid="mindset-history-card">
              <CardContent className="p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {fr ? 'Tes 14 derniers jours' : 'Your last 14 days'}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    <span className="uppercase tracking-widest mr-1">{fr ? 'Total' : 'Total'}</span>
                    <span className="font-bold text-foreground">{history.total_answered}</span>
                  </p>
                </div>
                <div className="h-20 flex items-end gap-1">
                  {history.bars.map((b, i) => {
                    const hit = !!b.answered;
                    return (
                      <div key={b.date} className="flex-1 flex flex-col items-center gap-1 min-w-0 h-full">
                        <div className="relative w-full flex-1 flex items-end">
                          <div
                            className={`w-full rounded-t transition-all duration-500 ${
                              hit
                                ? 'bg-gradient-to-t from-orange-600 via-orange-500 to-amber-300'
                                : 'bg-zinc-700/70'
                            }`}
                            style={{
                              height: hit ? '100%' : '15%',
                              boxShadow: hit ? '0 0 10px rgba(251,146,60,0.55), 0 0 3px rgba(245,158,11,0.45)' : 'none',
                            }}
                            title={`${b.date}: ${hit ? '✓' : '—'}`}
                          />
                        </div>
                        <span className={`text-[8px] ${hit ? 'text-amber-400 font-bold' : 'text-muted-foreground'}`}>
                          {b.date.slice(-2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {streak >= 3 && (
                  <p className="mt-3 text-center text-[11px] uppercase tracking-widest text-amber-400 font-bold">
                    🔥 {fr ? `${streak} jours d'affilée — continue` : `${streak} days in a row — keep going`}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* 4. Bilan du mois — moved to the end per user request */}
            <MindsetMonthlyRecap />

            {/* Why it matters */}
            <Card className="border-border/40 bg-card/40">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  {fr ? 'Pourquoi ce rituel ?' : 'Why this ritual?'}
                </p>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  {fr
                    ? 'Définir une intention claire au lever active tes circuits préfrontaux. Sur 30 jours, tu construis 3 badges et tu te connais mieux que jamais.'
                    : 'Setting a clear intention in the morning activates your prefrontal circuits. Over 30 days, you earn 3 badges and you know yourself better than ever.'}
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Coach YUMIX feedback modal — empathic reply after each Mindset answer */}
      <AnimatePresence>
        {coachReply && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setCoachReply(null)}
            data-testid="mindset-coach-reply-overlay"
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 22, stiffness: 280 }}
              className="relative w-full max-w-md rounded-2xl border border-amber-400/50 bg-gradient-to-br from-zinc-950 via-zinc-900 to-orange-950/30 p-6"
              style={{ boxShadow: '0 0 32px rgba(251,146,60,0.45), 0 0 12px rgba(245,158,11,0.55), inset 0 0 0 1px rgba(251,146,60,0.15)' }}
              onClick={(e) => e.stopPropagation()}
              data-testid="mindset-coach-reply-modal"
            >
              {/* Close button */}
              <button
                onClick={() => setCoachReply(null)}
                className="absolute top-3 right-3 w-7 h-7 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center transition-colors"
                data-testid="mindset-coach-reply-close"
              >
                <X className="w-3.5 h-3.5 text-zinc-400" />
              </button>

              {/* Header — Coach YUMIX avatar bubble */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center"
                  style={{ boxShadow: '0 0 18px rgba(251,146,60,0.7)' }}
                >
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-400">
                    {fr ? 'Coach YUMIX réagit' : 'Coach YUMIX reacts'}
                  </p>
                  <p className="font-heading text-lg uppercase tracking-tight text-white leading-none mt-0.5">
                    {coachReply.tone === 'celebratory'
                      ? (fr ? 'Bravo' : 'Bravo')
                      : coachReply.tone === 'grounding'
                        ? (fr ? 'Recentré' : 'Grounded')
                        : (fr ? 'Je t’écoute' : 'I hear you')}
                  </p>
                </div>
              </div>

              {/* Coach message */}
              <p
                className="text-base text-foreground/95 leading-relaxed mb-5"
                data-testid="mindset-coach-reply-message"
              >
                {coachReply.message}
              </p>

              {/* XP + streak chips */}
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {coachReply.points_earned > 0 && (
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500/25 to-amber-500/20 ring-1 ring-orange-400/60"
                    style={{ boxShadow: '0 0 10px rgba(251,146,60,0.45)' }}
                  >
                    <Sparkles className="w-3 h-3 text-orange-300" />
                    <span className="text-xs font-black uppercase tracking-widest text-orange-200">
                      +{coachReply.points_earned} XP
                    </span>
                  </span>
                )}
                {coachReply.streak_days >= 1 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 ring-1 ring-amber-400/40">
                    <Flame className="w-3 h-3 text-amber-400" />
                    <span className="text-xs font-black uppercase tracking-widest text-amber-300">
                      {coachReply.streak_days}{fr ? ' j' : ' d'}
                    </span>
                  </span>
                )}
              </div>

              {/* CTA */}
              <Button
                onClick={() => setCoachReply(null)}
                className="w-full bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-600 text-white font-bold uppercase tracking-widest"
                style={{ boxShadow: '0 0 14px rgba(251,146,60,0.55)' }}
                data-testid="mindset-coach-reply-cta"
              >
                {fr ? 'Compris, on avance' : "Got it, let's go"}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}
